/**
 * Responsibility: describe the ring aircraft as a wireframe vertex and edge list.
 *
 * A hexagonal ring on a short spine — no wings at all. The third silhouette needs to differ from
 * both a triangle and a needle, and in a world made entirely of straight glowing lines a closed
 * curved-looking outline is the one shape nothing else in the world has.
 *
 * Model space matches `default.js`: nose down negative Z, +Y up, +X the right wing, roughly two
 * units long.
 */

/** Points around the ring, as [x, y] pairs on a unit circle. Six is enough to read as round. */
const RING_POINTS = 6;
const RING_RADIUS = 0.52;

/** The ring sits behind the nose, at this Z. */
const RING_Z = 0.35;

/**
 * Build the ring's vertices.
 *
 * Generated rather than typed out because a hexagon's coordinates are irrational and a hand-written
 * approximation would leave the ring visibly lopsided. Every other model is hand-authored; this one
 * is the exception because the shape is defined by a rule rather than by taste.
 */
function buildVertices() {
  const vertices = [
    [0, 0, -1.15], // 0 nose
  ];

  for (let i = 0; i < RING_POINTS; i += 1) {
    const angle = (i / RING_POINTS) * Math.PI * 2;
    vertices.push([Math.cos(angle) * RING_RADIUS, Math.sin(angle) * RING_RADIUS, RING_Z]);
  }

  return vertices;
}

/** Ring edges plus the spokes back to the nose. */
function buildEdges() {
  const edges = [];

  for (let i = 0; i < RING_POINTS; i += 1) {
    const current = 1 + i;
    const next = 1 + ((i + 1) % RING_POINTS);
    edges.push([current, next]);
  }

  /*
    Spokes to alternating ring points only. Six would crowd the middle and the shape would read as a
    solid blob at distance; three keeps the ring open.
  */
  for (let i = 0; i < RING_POINTS; i += 2) {
    edges.push([0, 1 + i]);
  }

  return edges;
}

export const ringAircraft = {
  id: 'ring',
  label: 'Ring',

  vertices: buildVertices(),
  edges: buildEdges(),
};
